/**
 * Extracts keys from TFieldValues whose non-nullable type extends TValue.
 *
 * This type utility helps with strongly-typed form field selection based on their value types,
 * particularly useful when working with useFormValues to select fields by their structural types.
 *
 * @template TFieldValues - The form values object type.
 * @template TValue - The target value type to match against.
 * @returns Keys of TFieldValues whose non-nullable values are assignable to TValue.
 *
 * @example
 * // usage with structural objects
 * type Address = {
 *   street: string;
 *   city: string;
 *   zipCode: string;
 * };
 *
 * type ComplexForm = {
 *   personalInfo: {
 *     firstName: string;
 *     lastName: string;
 *   };
 *   homeAddress: Address;
 *   workAddress: Address | null;
 *   shippingAddress?: Address;
 * };
 *
 * // Results in: "homeAddress" | "workAddress" | "shippingAddress"
 * type AddressFields = NonNullableFieldPathByValue<ComplexForm, Address>;
 *
 * // Usage with useFormValues:
 * const addressFields = useFormValues<ComplexForm, AddressFields>(['homeAddress', 'workAddress']);
 */
export type NonNullableFieldPathByValue<TFieldValues, TValue> = Extract<
  {
    [Key in keyof TFieldValues]: NonNullable<TFieldValues[Key]> extends TValue ? Key : never;
  }[keyof TFieldValues],
  string
>;
